import { AbstractBuildstep, type OptionDB } from "@project/emerge";
import { Sides } from "@project/emerge-types";
import * as THREE from "three";
import { Vector2, Vector3 } from "three";
import { BranchOrientation } from "../enum/BranchOrientation";
import { Covers } from "../enum/Covers";
import { InteriorType } from "../enum/InteriorType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { LTProducts } from "../enum/LTProducts";
import { PaddingCubeSide } from "../enum/PaddingCubeSide";
import { PaddingStyle } from "../enum/PaddingStyle";
import { LTPartType } from "../enum/PartType";
import type { CaseInteriorOptions } from "../interfaces/AccessoryCompartmentOptions";
import type { CaseCompartment } from "../interfaces/CaseCompartment";
import type { CaseSeparator } from "../interfaces/CaseSeparator";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { PadDefinition } from "../interfaces/PadProperties";
import type { TrayPanelDefinition } from "../interfaces/TrayPanelProperties";
import type { VolumeDefinition } from "../interfaces/VolumeProperties";
import { DefaultGroupName } from "../structure/Rect";
import { RectPropertiesBySide } from "../structure/RectProperties";
import {
  calculateBoxWithPadding,
  resolvePaddingStyleMap,
} from "../user_options/InteriorOptions";
import {
  createInteriorPadCutouts,
  createTrayPanelCutouts,
  serializeExtendedCutouts,
} from "./interior-helpers/CutoutHelpers";
import {
  calculatePanelsAndVolumes,
  calculateProfileOffset,
  ensureStrength,
  getDefaultPanelColor,
  getToleranceWithFallback,
  getTrayMaterialWithFallback,
  getVector3Index,
  resolveAccessoryPlacement,
} from "./interior-helpers/InteriorHelpers";
import type {
  InteriorPadDependencyData,
  InteriorTrayPanelDependencyData,
  InteriorVolumeDependencyData,
} from "./interior-helpers/InteriorTypes";
import {
  createPaddedCube,
  getPaddingPartSelection,
  type PadEdgeCutoutPoint,
} from "./interior-helpers/PaddingHelpers";
import { applyPadVolumeCenterWorldOffset } from "./interior-helpers/padPositionOffset";

// Union type for all dependency data this buildstep produces
type CaseInteriorDependencyData =
  | InteriorVolumeDependencyData
  | InteriorTrayPanelDependencyData
  | InteriorPadDependencyData;

// Union type for all part definitions this buildstep produces
type CaseInteriorPartDefinition =
  | VolumeDefinition
  | TrayPanelDefinition
  | PadDefinition;

/**
 * How far a piece of interior geometry moves to make room for the accessory
 * compartment. A wall-mounted compartment shifts everything the same way, while
 * a centred one pushes contents apart, so the shift depends on which side of
 * the centre line the piece sits on.
 */
type AccessoryOffsetResolver = (center: Vector3) => Vector3;

const ZERO_ACCESSORY_OFFSET = new Vector3();

/**
 * Shift an X/Z position from the padded compartment centre to the inner-cavity
 * centre. Side padding may be asymmetric, so the usable cavity (and everything
 * authored relative to it — the content cube and the additional foam) is offset
 * from the padded centre. This is the single source of truth for that offset so
 * content and additional foam stay positioned identically (#131).
 */
function applyCavityCentreOffset(
  pos: Vector3,
  volume: CaseCompartment,
): Vector3 {
  pos.x -= (volume.padding[Sides.RIGHT] - volume.padding[Sides.LEFT]) / 2;
  pos.z -= (volume.padding[Sides.FRONT] - volume.padding[Sides.BACK]) / 2;
  return pos;
}

/**
 * CaseInteriorBuildStep handles all interior parts for CASE interior type:
 * - Volumes (compartments)
 * - Tray panels (dividers)
 * - Pads (padding cubes)
 * - Cover padding
 *
 * This buildstep is used by base product types: Truhe, Haube, Koffer, KeyboardCase
 */
export class CaseInteriorBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  CaseInteriorPartDefinition,
  CaseInteriorDependencyData
> {
  constructor(private readonly optionDb: OptionDB) {
    super("CaseInteriorBuildStep");
  }

  // ===== VOLUME METHODS =====

  private calculateVolumeCenterY(
    volume: CaseCompartment,
    settings: FlightCaseSettings,
    blueprint: FlightCaseBlueprint,
  ): number {
    const halfHeight = volume.sizeWithoutPadding.y / 2;
    const innerY = blueprint.innerDimensions.y;
    const bottomPad = volume.padding[Sides.BOTTOM] ?? 0;

    if (bottomPad > 0) {
      // emerge3#114: anchor on bottom foam slab, not case floor.
      return -innerY / 2 + bottomPad + halfHeight;
    }

    const toleranceY =
      settings.interiorOptions.type === InteriorType.CASE
        ? (settings.interiorOptions.tolerance?.y ?? 0)
        : 0;
    // Top-anchor fallback always reads Deckel foam (COVER), including Haube/KeyboardCase.
    // Other interior paths use BASE for hood products (panel height, top padding cubes).
    const coverPad =
      settings.coverPaddings[Covers.COVER]?.areaPaddingType?.strength ?? 0;

    return innerY / 2 - coverPad - toleranceY - halfHeight;
  }

  private calculateVolumeDeps(
    volume: CaseCompartment,
    settings: FlightCaseSettings,
    blueprint: FlightCaseBlueprint,
    accessoryOffset: AccessoryOffsetResolver,
    dependencies: Map<string, CaseInteriorDependencyData>,
    counter: { value: number },
  ): Vector3 {
    // Volume content sits at the compartment (slot) center in X/Z; Y is
    // re-anchored on the bottom foam / cover below.
    const position = volume.center.clone().add(accessoryOffset(volume.center));
    position.y = this.calculateVolumeCenterY(volume, settings, blueprint);

    // The "Inhalt" cube has the inner-cavity size (sizeWithoutPadding). With
    // asymmetric side padding the cavity is offset from the padded compartment
    // centre, so place the cube at the cavity centre — the exact same rule the
    // additional-foam cubes use (see applyCavityCentreOffset). The returned
    // `position` stays the padded centre so wall padding and the additional-foam
    // base are unaffected (the latter applies the cavity offset itself).
    const contentPosition = applyCavityCentreOffset(position.clone(), volume);

    const partId = `volume_${counter.value}_${counter.value++}`;
    const dep: InteriorVolumeDependencyData = {
      partId,
      partKind: "InteriorVolume",
      type: LTPartType.Volume,
      group: DefaultGroupName,
      buildstepName: this.buildstepName,
      name: "Volume",
      partName: "Volume",
      position: contentPosition.clone(),
      size: [
        volume.sizeWithoutPadding.x,
        volume.sizeWithoutPadding.y,
        volume.sizeWithoutPadding.z,
      ],
      sizeLabel: `${volume.sizeWithPadding.x} mm × ${volume.sizeWithPadding.z} mm`,
      orientation: 0,
    };
    dependencies.set(partId, dep);
    return position;
  }

  // ===== TRAY PANEL METHODS =====

  private calculateAccessoryPanelDeps(
    settings: FlightCaseSettings,
    blueprint: FlightCaseBlueprint,
    panelHeight: number,
    dependencies: Map<string, CaseInteriorDependencyData>,
    counter: { value: number },
    volumes: CaseCompartment[],
  ): { resolve: AccessoryOffsetResolver; placement: "WALL" | "CENTER" } | null {
    const { accessoryCompartment, trayMaterial } =
      settings.interiorOptions as CaseInteriorOptions;

    if (!accessoryCompartment) {
      return null;
    }

    let effectiveTrayMaterial = trayMaterial;
    if (!effectiveTrayMaterial) {
      console.warn(
        "[CaseInteriorBuildStep] Missing trayMaterial for accessory compartment. Using default.",
      );
      effectiveTrayMaterial = this.optionDb.getByName(
        LTCaseUserOption.TrayMaterialType,
        "PLW1415",
      );
      if (!effectiveTrayMaterial) {
        console.warn(
          "[CaseInteriorBuildStep] Could not load default trayMaterial. Skipping accessory compartment.",
        );
        return null;
      }
    }

    const rectProps = RectPropertiesBySide[accessoryCompartment.side];
    if (!rectProps) return null;
    const { horizontal, normal, normalDir } = rectProps;

    const panelColor = getDefaultPanelColor();
    const panelSize = new Vector2(
      blueprint.innerDimensions[horizontal],
      panelHeight,
    );
    const emitPanel = (panelPosition: Vector3) => {
      const panelId = `accessory_panel_${counter.value++}`;
      panelPosition.y = -blueprint.innerDimensions.y / 2 + panelHeight / 2;
      const cutouts = createTrayPanelCutouts(
        this.optionDb,
        settings,
        blueprint,
        panelHeight,
        panelPosition,
        blueprint.innerDimensions[horizontal],
        accessoryCompartment.side,
        [],
      );

      dependencies.set(panelId, {
        partId: panelId,
        partKind: "InteriorTrayPanel",
        type: LTPartType.TrayPanel,
        group: DefaultGroupName,
        buildstepName: this.buildstepName,
        material: trayMaterial,
        color: panelColor,
        position: panelPosition,
        side: accessoryCompartment.side,
        size: new Vector3(panelSize.x, 0, panelSize.y),
        orientation: 0,
        cutouts: serializeExtendedCutouts(cutouts),
      } satisfies InteriorTrayPanelDependencyData);
    };

    const padding = accessoryCompartment.padding?.strength ?? 0;
    const size = accessoryCompartment.size ?? 0;

    const axisIndex = getVector3Index(normal);
    const placement = resolveAccessoryPlacement(
      accessoryCompartment,
      volumes,
      axisIndex,
    );

    if (accessoryCompartment.placement === "CENTER" && placement === "WALL") {
      console.warn(
        "[CaseInteriorBuildStep] Centred accessory compartment needs content on both sides of the centre line. Falling back to a wall-mounted compartment.",
      );
    }

    if (placement === "CENTER") {
      // Two dividers around the middle; the contents part to either side. The
      // content divider that sat on the centre line is dropped — these two
      // replace it (see calculateDividerPanelDeps).
      const halfCavity = size / 2 + padding;
      const dividerDistance = halfCavity + effectiveTrayMaterial.strength / 2;

      for (const sign of [-1, 1]) {
        const position = new Vector3();
        position.setComponent(axisIndex, sign * dividerDistance);
        emitPanel(position);
      }

      // The halves already stood one divider apart, so they only have to move
      // out by the rest: half the cavity plus half of the wall replacing it.
      const shift = dividerDistance;
      return {
        placement: "CENTER",
        resolve: (center: Vector3) => {
          const offset = new Vector3();
          offset.setComponent(
            axisIndex,
            center.getComponent(axisIndex) < 0 ? -shift : shift,
          );
          return offset;
        },
      };
    }

    const accessoryExtraWidth =
      size + 2 * padding + effectiveTrayMaterial.strength;
    const distanceFromCenter =
      blueprint.innerDimensions[normal] / 2 -
      accessoryExtraWidth +
      effectiveTrayMaterial.strength / 2;

    emitPanel(normalDir.clone().multiplyScalar(distanceFromCenter));

    const newAccessoryOffset = normalDir
      .clone()
      .multiplyScalar(-accessoryExtraWidth / 2);
    return { placement: "WALL", resolve: () => newAccessoryOffset };
  }

  /**
   * True for the content divider a centred accessory compartment supersedes:
   * the one on the centre line that separates along the compartment's own axis.
   */
  private isReplacedByCentreCompartment(
    panel: CaseSeparator,
    settings: FlightCaseSettings,
  ): boolean {
    const interior = settings.interiorOptions;
    if (interior.type !== InteriorType.CASE) return false;
    const side = interior.accessoryCompartment?.side;
    if (side === undefined) return false;
    const normal = RectPropertiesBySide[side]?.normal;
    if (!normal) return false;

    // Horizontal branches separate along x, vertical ones along z.
    const panelNormal =
      panel.branchOrientation === BranchOrientation.Horizontal ? "x" : "z";
    if (panelNormal !== normal) return false;

    return Math.abs(panel.center[normal]) < 0.5;
  }

  private calculateDividerPanelDeps(
    panel: {
      branchOrientation: BranchOrientation;
      center: Vector3;
      width: number;
      crossSections: Vector3[];
    },
    settings: FlightCaseSettings,
    blueprint: FlightCaseBlueprint,
    panelHeight: number,
    accessoryOffset: AccessoryOffsetResolver,
    dependencies: Map<string, CaseInteriorDependencyData>,
    counter: { value: number },
  ) {
    const interior = settings.interiorOptions;
    if (interior.type !== InteriorType.CASE) return;
    const { trayMaterial } = interior;

    if (!trayMaterial) {
      return;
    }

    const panelId = `divider_panel_${counter.value++}`;
    const panelPosition = panel.center
      .clone()
      .add(accessoryOffset(panel.center));
    panelPosition.y = -blueprint.innerDimensions.y / 2 + panelHeight / 2;
    const panelSize = new Vector2(panel.width, panelHeight);
    const panelColor = getDefaultPanelColor();

    const cutouts = createTrayPanelCutouts(
      this.optionDb,
      settings,
      blueprint,
      panelHeight,
      panelPosition,
      panel.width,
      panel.branchOrientation === BranchOrientation.Horizontal
        ? Sides.LEFT
        : Sides.FRONT,
      panel.crossSections.map((c: Vector3) =>
        c.clone().add(accessoryOffset(c)),
      ),
    );

    const dep: InteriorTrayPanelDependencyData = {
      partId: panelId,
      partKind: "InteriorTrayPanel",
      type: LTPartType.TrayPanel,
      group: DefaultGroupName,
      buildstepName: this.buildstepName,
      material: trayMaterial,
      color: panelColor,
      position: panelPosition,
      side:
        panel.branchOrientation === BranchOrientation.Horizontal
          ? Sides.LEFT
          : Sides.FRONT,
      size: new Vector3(panelSize.x, 0, panelSize.y),
      orientation: 0,
      cutouts: serializeExtendedCutouts(cutouts),
    };

    dependencies.set(panelId, dep);
  }

  // ===== PADDING METHODS =====

  private buildInteriorPadCutouts(
    settings: FlightCaseSettings,
    blueprint: FlightCaseBlueprint,
    panelHeight: number,
    side: Sides,
    edgeCutoutPoints: PadEdgeCutoutPoint[],
  ): InteriorPadDependencyData["cutouts"] {
    return serializeExtendedCutouts(
      createInteriorPadCutouts(
        settings,
        blueprint,
        panelHeight,
        side,
        edgeCutoutPoints,
      ),
      { interiorEdge: true },
    );
  }

  private calculateAccessoryPaddingDeps(
    settings: FlightCaseSettings,
    blueprint: FlightCaseBlueprint,
    panelHeight: number,
    paddingHeights: { outerPaddingHeight: number; innerPaddingHeight: number },
    accessoryOffset: AccessoryOffsetResolver,
    effectivePlacement: "WALL" | "CENTER",
    dependencies: Map<string, CaseInteriorDependencyData>,
    counter: { value: number },
  ): void {
    const { outerPaddingHeight, innerPaddingHeight } = paddingHeights;
    const interior = settings.interiorOptions;
    if (interior.type !== InteriorType.CASE) return;
    const { accessoryCompartment, trayMaterial } = interior;

    if (
      !accessoryCompartment ||
      !trayMaterial ||
      accessoryCompartment.side === undefined ||
      !accessoryCompartment.padding ||
      accessoryCompartment.padding.strength <= 0
    ) {
      return;
    }

    const s = accessoryCompartment.padding.strength;
    const rectProps = RectPropertiesBySide[accessoryCompartment.side];
    if (!rectProps) return;
    const { horizontal, normal, normalDir } = rectProps;

    const size = new Vector3();
    size.setComponent(
      getVector3Index(normal),
      accessoryCompartment.size + 2 * s,
    );
    size.y = innerPaddingHeight;
    size.setComponent(
      getVector3Index(horizontal),
      blueprint.innerDimensions[horizontal],
    );

    const pos = new Vector3();
    pos.y = innerPaddingHeight / 2 - blueprint.innerDimensions.y / 2;
    if (effectivePlacement === "CENTER") {
      // The cavity straddles the centre line, so its padding does too.
      pos.setComponent(getVector3Index(normal), 0);
    } else {
      const wallOffset = accessoryOffset(new Vector3());
      pos.setComponent(
        getVector3Index(normal),
        blueprint.innerDimensions.clone().dot(normalDir) / 2 +
          wallOffset[normal] +
          (normalDir[normal] * trayMaterial.strength) / 2,
      );
    }

    const paddingMap: { [side in Sides]: number } = {
      [Sides.BOTTOM]: s,
      [Sides.BACK]: s,
      [Sides.FRONT]: s,
      [Sides.LEFT]: s,
      [Sides.RIGHT]: s,
      [Sides.TOP]: 0,
    };

    const padProperties = createPaddedCube(
      blueprint.innerDimensions,
      paddingMap,
      pos,
      size,
      outerPaddingHeight,
      {
        buildstepName: this.buildstepName,
        optionDb: this.optionDb,
        innerPaddingHeight,
        style: accessoryCompartment.padding.style,
      },
    );

    for (const padProps of padProperties) {
      // Skip pads with 0mm strength
      if (padProps.strength <= 0) {
        continue;
      }
      const padId = `accessory_pad_${counter.value++}`;
      const dep: InteriorPadDependencyData = {
        partId: padId,
        partKind: "InteriorPad",
        type: LTPartType.Pad,
        group: DefaultGroupName,
        buildstepName: this.buildstepName,
        position: padProps.position,
        side: padProps.side,
        size: padProps.size,
        strength: padProps.strength,
        style: padProps.style,
        orientation: 0,
        partSelection: padProps.partSelection,
        cutouts: this.buildInteriorPadCutouts(
          settings,
          blueprint,
          panelHeight,
          padProps.side,
          padProps.edgeCutoutPoints,
        ),
      };
      dependencies.set(padId, dep);
    }
  }

  private calculateVolumePaddingDeps(
    volume: CaseCompartment,
    volumePosition: Vector3,
    buildContext: {
      settings: FlightCaseSettings;
      blueprint: FlightCaseBlueprint;
      panelHeight: number;
      paddingHeights: {
        outerPaddingHeight: number;
        innerPaddingHeight: number;
      };
    },
    dependencies: Map<string, CaseInteriorDependencyData>,
    counter: { value: number },
  ): void {
    const { settings, blueprint, panelHeight, paddingHeights } = buildContext;
    const { outerPaddingHeight, innerPaddingHeight } = paddingHeights;
    const vol = calculateBoxWithPadding(volume);
    const pos = volumePosition.clone();
    pos.y = innerPaddingHeight / 2 - blueprint.innerDimensions.y / 2;

    const caseInterior = settings.interiorOptions as CaseInteriorOptions;
    const defaultPaddingStyle =
      caseInterior.paddingStyle ?? PaddingStyle.STANDARD;
    const padProperties = createPaddedCube(
      blueprint.innerDimensions,
      volume.padding,
      pos,
      vol.getSize(new Vector3()),
      outerPaddingHeight,
      {
        buildstepName: this.buildstepName,
        optionDb: this.optionDb,
        innerPaddingHeight,
        styleBySide: resolvePaddingStyleMap(
          volume.paddingStyle,
          defaultPaddingStyle,
        ),
      },
    );

    for (const padProps of padProperties) {
      // Skip pads with 0mm strength
      if (padProps.strength <= 0) {
        continue;
      }
      const padId = `volume_pad_${counter.value++}`;
      const dep: InteriorPadDependencyData = {
        partId: padId,
        partKind: "InteriorPad",
        type: LTPartType.Pad,
        group: DefaultGroupName,
        buildstepName: this.buildstepName,
        position: padProps.position,
        side: padProps.side,
        size: padProps.size,
        strength: padProps.strength,
        style: padProps.style,
        orientation: 0,
        partSelection: padProps.partSelection,
        cutouts: this.buildInteriorPadCutouts(
          settings,
          blueprint,
          panelHeight,
          padProps.side,
          padProps.edgeCutoutPoints,
        ),
      };
      dependencies.set(padId, dep);
    }
  }

  private calculateAdditionalPaddingDeps(
    volume: CaseCompartment,
    volumePosition: Vector3,
    settings: FlightCaseSettings,
    blueprint: FlightCaseBlueprint,
    isHood: boolean,
    context: {
      dependencies: Map<string, CaseInteriorDependencyData>;
      knownPaddingCubeMeasurements: string[];
      counter: { value: number };
    },
  ): void {
    const { dependencies, knownPaddingCubeMeasurements, counter } = context;
    if (
      !volume.additionalPaddingCubes ||
      volume.additionalPaddingCubes.length === 0
    ) {
      return;
    }

    const pos = volumePosition.clone();

    // Additional foam cubes are authored relative to the inner cavity centre
    // (item width/depth, padding excluded). With asymmetric side padding the
    // cavity is offset from the padded compartment centre, so shift the base to
    // the cavity centre — the same rule the content cube uses (#131).
    applyCavityCentreOffset(pos, volume);

    const cutHeight = settings.cuts?.[0]?.offset ?? 0;
    const { extra } = settings.exteriorOptions.profiles;
    const profileOffset = calculateProfileOffset(
      settings.exteriorOptions.profiles,
      isHood || settings.interiorOptions.upperPaddingAlignment,
    );
    const verticalCoverAreaPadding =
      settings.coverPaddings[isHood ? Covers.BASE : Covers.COVER]
        ?.areaPaddingType?.strength ?? 0;
    const panelHeight = isHood
      ? blueprint.innerDimensions.y - verticalCoverAreaPadding
      : blueprint.innerDimensions.y - cutHeight - extra + profileOffset;
    const innerPaddingHeight = panelHeight;

    pos.y = innerPaddingHeight / 2 - blueprint.innerDimensions.y / 2;
    const baseY = innerPaddingHeight / 2 - blueprint.innerDimensions.y / 2;

    for (const cube of volume.additionalPaddingCubes) {
      const acPos = pos.clone().add(new Vector3(cube.x, 0, cube.y));
      acPos.y = this.resolveCubeY(
        baseY,
        cube,
        settings,
        blueprint,
        volume.padding[Sides.BOTTOM],
        isHood,
      );

      const measurementsAsString = `${cube.width}x${cube.height}x${cube.depth}`;
      let index = knownPaddingCubeMeasurements.indexOf(measurementsAsString);
      if (index === -1) {
        index = knownPaddingCubeMeasurements.push(measurementsAsString) - 1;
      }

      // Skip pads with 0mm strength
      if (cube.height <= 0) {
        continue;
      }

      const padId = `additional_pad_${counter.value++}`;
      const groupName = this.resolveGroupName(cube.side, isHood);

      const dep: InteriorPadDependencyData = {
        partId: padId,
        partKind: "InteriorPad",
        type: LTPartType.Pad,
        group: groupName,
        buildstepName: this.buildstepName,
        position: acPos,
        side: cube.side === PaddingCubeSide.bottom ? Sides.BOTTOM : Sides.TOP,
        size: [cube.width, cube.depth],
        strength: cube.height,
        style: cube.style,
        name: String.fromCharCode(65 + index),
        orientation: 0,
        partSelection: getPaddingPartSelection(
          this.optionDb,
          cube.style,
          cube.height,
        ),
      };
      dependencies.set(padId, dep);
    }
  }

  private resolveGroupName(cubeSide: PaddingCubeSide, isHood: boolean): string {
    if (cubeSide === PaddingCubeSide.bottom) return DefaultGroupName;
    return isHood ? Covers.BASE : Covers.COVER;
  }

  private resolveCubeY(
    _baseY: number,
    cube: { side: PaddingCubeSide },
    settings: FlightCaseSettings,
    blueprint: FlightCaseBlueprint,
    bottomPaddingStrength: number,
    isHood: boolean,
  ): number {
    if (cube.side === PaddingCubeSide.bottom) {
      return -blueprint.innerDimensions.y / 2 + bottomPaddingStrength;
    }
    const cover = settings.coverPaddings[isHood ? Covers.BASE : Covers.COVER];
    const areaPad = cover?.areaPaddingType?.strength ?? 0;
    return blueprint.innerDimensions.y / 2 - areaPad;
  }

  // ===== MAIN BUILD METHOD =====

  private buildCaseInterior(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
    dependencies: Map<string, CaseInteriorDependencyData>,
    counter: { value: number },
    knownPaddingCubeMeasurements: string[],
  ): void {
    const interior = settings.interiorOptions;
    if (interior.type !== InteriorType.CASE) return;
    const isHood =
      settings.type === LTProducts.Haube ||
      settings.type === LTProducts.KeyboardCase;
    const { extra } = settings.exteriorOptions.profiles;
    let accessoryOffset: AccessoryOffsetResolver = () => ZERO_ACCESSORY_OFFSET;
    let effectivePlacement: "WALL" | "CENTER" = "WALL";
    const {
      accessoryCompartment,
      tolerance,
      trayMaterial,
      upperPaddingAlignment,
    } = interior;

    const finalTrayMaterial = getTrayMaterialWithFallback(
      this.optionDb,
      trayMaterial,
      this.buildstepName,
    );
    if (!finalTrayMaterial) return;

    const finalTolerance = getToleranceWithFallback(
      tolerance,
      this.buildstepName,
    );

    const calculationResult = calculatePanelsAndVolumes(
      interior,
      finalTrayMaterial,
      finalTolerance,
      this.buildstepName,
    );
    if (!calculationResult) return;

    const { panels, volumes } = calculationResult as {
      panels: Array<{
        branchOrientation: BranchOrientation;
        center: Vector3;
        width: number;
        crossSections: Vector3[];
      }>;
      volumes: CaseCompartment[];
      mode: string;
    };

    const cutHeight = settings.cuts?.[0]?.offset ?? 0;
    const profileOffset = calculateProfileOffset(
      settings.exteriorOptions.profiles,
      isHood || upperPaddingAlignment,
    );
    const verticalCoverAreaPadding =
      settings.coverPaddings[isHood ? Covers.BASE : Covers.COVER]
        ?.areaPaddingType?.strength ?? 0;
    const panelHeight = isHood
      ? blueprint.innerDimensions.y - verticalCoverAreaPadding
      : blueprint.innerDimensions.y - cutHeight - (extra ?? 0) + profileOffset;
    const innerPaddingHeight = panelHeight;
    const outerPaddingHeight = isHood
      ? cutHeight +
        calculateProfileOffset(settings.exteriorOptions.profiles, true) -
        1
      : innerPaddingHeight;

    // Accessory panel
    if (accessoryCompartment) {
      const calculatedOffset = this.calculateAccessoryPanelDeps(
        settings,
        blueprint,
        panelHeight,
        dependencies,
        counter,
        volumes,
      );
      if (calculatedOffset) {
        accessoryOffset = calculatedOffset.resolve;
        effectivePlacement = calculatedOffset.placement;
      }

      // Accessory padding
      this.calculateAccessoryPaddingDeps(
        settings,
        blueprint,
        panelHeight,
        { outerPaddingHeight, innerPaddingHeight },
        accessoryOffset,
        effectivePlacement,
        dependencies,
        counter,
      );
    }

    // Divider panels
    for (const panel of panels) {
      // A centred accessory compartment brings its own two walls, which stand
      // where this divider used to. Emitting it as well would leave two boards
      // overlapping beside the compartment.
      if (
        effectivePlacement === "CENTER" &&
        this.isReplacedByCentreCompartment(panel, settings)
      ) {
        continue;
      }
      this.calculateDividerPanelDeps(
        panel,
        settings,
        blueprint,
        panelHeight,
        accessoryOffset,
        dependencies,
        counter,
      );
    }

    // Volumes and their padding
    for (const volume of volumes) {
      // Volume
      const volumePosition = this.calculateVolumeDeps(
        volume,
        settings,
        blueprint,
        accessoryOffset,
        dependencies,
        counter,
      );

      // Volume padding
      this.calculateVolumePaddingDeps(
        volume,
        volumePosition,
        {
          settings,
          blueprint,
          panelHeight,
          paddingHeights: { outerPaddingHeight, innerPaddingHeight },
        },
        dependencies,
        counter,
      );

      // Additional padding cubes
      this.calculateAdditionalPaddingDeps(
        volume,
        volumePosition,
        settings,
        blueprint,
        isHood,
        {
          dependencies,
          knownPaddingCubeMeasurements,
          counter,
        },
      );
    }
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, CaseInteriorDependencyData> {
    const dependencies = new Map<string, CaseInteriorDependencyData>();

    // Only process CASE interior type
    if (settings.interiorOptions.type !== InteriorType.CASE) {
      return dependencies;
    }

    const counter = { value: 0 };
    const knownPaddingCubeMeasurements: string[] = [];

    this.buildCaseInterior(
      blueprint,
      settings,
      dependencies,
      counter,
      knownPaddingCubeMeasurements,
    );

    // Note: Cover padding is now handled by CoverPaddingBuildStep for all interior types

    return dependencies;
  }

  public createPart(
    partId: string,
    dependencies: CaseInteriorDependencyData,
  ): CaseInteriorPartDefinition {
    switch (dependencies.partKind) {
      case "InteriorVolume":
        return this.createVolumePart(partId, dependencies);
      case "InteriorTrayPanel":
        return this.createTrayPanelPart(partId, dependencies);
      case "InteriorPad":
        return this.createPadPart(partId, dependencies);
    }
  }

  private createVolumePart(
    partId: string,
    dep: InteriorVolumeDependencyData,
  ): VolumeDefinition {
    return {
      id: partId,
      type: dep.type,
      group: dep.group,
      buildstepName: dep.buildstepName,
      name: dep.name,
      partName: dep.partName,
      position: dep.position.clone(),
      size: dep.size,
      sizeLabel: dep.sizeLabel,
      rotation: new THREE.Euler(0, 0, 0),
      orientation: dep.orientation,
    };
  }

  private createTrayPanelPart(
    partId: string,
    dep: InteriorTrayPanelDependencyData,
  ): TrayPanelDefinition {
    const trayPanelColor = dep.color ?? getDefaultPanelColor();
    const materialStrength = dep.material?.strength;
    const trayPanelStrength = ensureStrength(materialStrength, dep.material);

    return {
      id: partId,
      type: dep.type,
      group: dep.group,
      buildstepName: dep.buildstepName,
      color: trayPanelColor,
      position: dep.position.clone(),
      size: new Vector2(dep.size.x, dep.size.z),
      strength: trayPanelStrength,
      side: dep.side,
      rotation: this.rotateOntoSide(dep.side, dep.orientation),
      orientation: dep.orientation,
      cutouts: dep.cutouts ?? [],
      material: dep.material,
    };
  }

  private createPadPart(
    partId: string,
    dep: InteriorPadDependencyData,
  ): PadDefinition {
    // Additional safety check: skip pads with 0mm strength
    if (dep.strength <= 0) {
      // This should not happen if selectDependencies filters correctly
      throw new Error(
        `Pad ${partId} has 0mm strength and should have been filtered in selectDependencies`,
      );
    }

    const rotation = this.rotateOntoSide(dep.side, dep.orientation);
    return {
      id: partId,
      type: dep.type,
      group: dep.group,
      buildstepName: dep.buildstepName,
      position: applyPadVolumeCenterWorldOffset(
        dep.position,
        dep.strength,
        rotation,
      ),
      side: dep.side,
      size: dep.size,
      strength: dep.strength,
      style: dep.style,
      name: dep.name,
      partSelection: dep.partSelection,
      rotation,
      orientation: dep.orientation,
      cutouts: dep.cutouts ?? [],
    };
  }
}
